<template>
  <div>
    <h2 class="step-name">Contact Details</h2>

    <div class="alert alert-info">
      These contact details come from ezyVet and any changes made here will be pushed automatically back to ezyVet.
    </div>
    <div class="step-section">
      <div class="form-group">
        <div
          class="row"
          v-for="(contactdetail, index) in getFieldValue('CONTACT_DETAILS')"
          :key="`contactdetail__${index}`"
        >
          <div class="col-md-2">
            <text-input
              label="Name"
              :disabled="isReadOnly || !getFieldValue(`contactdetail_${contactdetail.id}-active`)"
              :vvalidate="getFieldValue(`contactdetail_${contactdetail.id}-active`) ? 'required' : ''"
              @on-change="handleContactsChange"
              :field-name="`contactdetail_${contactdetail.id}-name`"
              :show-input-box="false"
            >
            </text-input>
          </div>
          <div class="col-md-2">
            <text-input
              label="Value"
              :disabled="isReadOnly || !getFieldValue(`contactdetail_${contactdetail.id}-active`)"
              :vvalidate="getFieldValue(`contactdetail_${contactdetail.id}-active`) ? 'required' : ''"
              @on-change="handleContactsChange"
              required
              :field-name="`contactdetail_${contactdetail.id}-value`"
              :show-input-box="false"
            >
            </text-input>
          </div>
          <div class="col-md-2">
            <select-input
              label="Type"
              :disabled="isReadOnly || !getFieldValue(`contactdetail_${contactdetail.id}-active`)"
              :vvalidate="getFieldValue(`contactdetail_${contactdetail.id}-active`) ? 'required' : ''"
              required
              @on-change="handleContactsChange"
              :field-name="`contactdetail_${contactdetail.id}-contact_detail_type`"
              :options="contactDetailTypeOptions"
              :show-input-box="false"
            >
            </select-input>
          </div>
          <div class="col-md-2">
            <select-check-box-input
              label="Subscription Groups"
              @on-change="handleContactsChange"
              :options="subscriptionGroupsOptions"
              :field-name="`contactdetail_${contactdetail.id}-subscription_groups`"
              :show-input-box="false"
            >
            </select-check-box-input>
          </div>
          <div class="col-md-2">
            <check-box
              style="margin-top: 32px"
              label="Preferred"
              @on-change="handleContactsChange"
              :field-name="`contactdetail_${contactdetail.id}-preferred`"
              :disabled="isReadOnly || !getFieldValue(`contactdetail_${contactdetail.id}-active`)"
              :show-input-box="false"
            >
            </check-box>
          </div>
          <div class="col-md-2">
            <check-box
              style="margin-top: 32px; text-align: left"
              :label="getFieldValue(`contactdetail_${contactdetail.id}-active`) ? 'Enabled' : 'Disabled'"
              @on-change="toggleContactDetailActive(contactdetail)"
              :field-name="`contactdetail_${contactdetail.id}-active`"
              :show-input-box="false"
            >
            </check-box>
          </div>
        </div>
      </div>

      <div class="text-right" style="padding-right: 220px" v-if="!isReadOnly">
        <span
          class="glyphicon glyphicon-plus-sign"
          style="color: green; cursor: pointer"
          v-tooltip="'Add Contact Detail'"
          @click.prevent="canAdd ? addNewContactDetail() : ''"
          :disabled="!canAdd"
          :class="{ disabled: !canAdd }"
        >
        </span>
        Add New Contact
      </div>
      <br />
      <div class="form-group">
        <div class="alert alert-danger" v-if="!checkUnique()">
          <span class="glyphicon glyphicon-warning-sign"></span>
          Please make sure values are unique.
        </div>
        <div
          class="row"
          v-for="(contactdetail, index) in getFieldValue('NEW_CONTACT_DETAILS')"
          :key="`newcontactdetail__${index}`"
        >
          <div class="col-md-2">
            <text-input
              label="Name"
              required
              @on-change="handleContactsChange"
              :disabled="isReadOnly || !getFieldValue(`newcontactdetail_${contactdetail.frontend_id}-active`)"
              :field-name="`newcontactdetail_${contactdetail.frontend_id}-name`"
              :show-input-box="false"
            >
            </text-input>
          </div>
          <div class="col-md-2">
            <text-input
              label="Value"
              required
              :disabled="isReadOnly || !getFieldValue(`newcontactdetail_${contactdetail.frontend_id}-active`)"
              @on-change="handleContactsChange"
              :field-name="`newcontactdetail_${contactdetail.frontend_id}-value`"
              :show-input-box="false"
            >
            </text-input>
          </div>
          <div class="col-md-2">
            <select-input
              label="Type"
              required
              :disabled="isReadOnly || !getFieldValue(`newcontactdetail_${contactdetail.frontend_id}-active`)"
              @on-change="handleContactsChange"
              :field-name="`newcontactdetail_${contactdetail.frontend_id}-contact_detail_type`"
              :options="contactDetailTypeOptions"
              :show-input-box="false"
            >
            </select-input>
          </div>
          <div class="col-md-2">
            <select-check-box-input
              label="Subscription Groups"
              @on-change="handleContactsChange"
              :options="subscriptionGroupsOptions"
              :field-name="`newcontactdetail_${contactdetail.frontend_id}-subscription_groups`"
              :show-input-box="false"
            >
            </select-check-box-input>
          </div>
          <div class="col-md-2">
            <check-box
              label="Preferred"
              @on-change="handleContactsChange"
              :field-name="`newcontactdetail_${contactdetail.frontend_id}-preferred`"
              :show-input-box="false"
            >
            </check-box>
          </div>
          <div class="col-md-2 text-left" v-if="!isReadOnly">
            <span
              class="glyphicon glyphicon-minus-sign"
              v-tooltip="'Remove Contact Detail'"
              style="color: red; cursor: pointer; margin-top: 32px; text-align: left"
              @click.prevent="removeNewContactDetail(contactdetail)"
              :show-input-box="false"
            >
            </span>
            Remove Contact
          </div>
        </div>
        <template v-if="hasOldContactDetails">
          <div class="alert alert-info">
            These contact details were entered into previous consults but were not saved to ezyVet. Do you want to save
            them to ezyVet this time? If saving, please add details of communications and reminders to the 'Changes to
            be posted to Farm Services' comment box below.
          </div>
          <div
            class="row"
            v-for="(contactdetail, index) in getFieldValue('OLD_CONTACT_DETAILS')"
            :key="`oldcontactdetail__${index}`"
          >
            <div class="col-md-3">
              <div class="form-group">
                <span class="form-group">
                  <label for="">Name</label>
                </span>
                <input class="form-control" type="text" :value="contactdetail.name" disabled />
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <span class="form-group">
                  <label for="">Value</label>
                </span>
                <input class="form-control" type="text" :value="contactdetail.value" disabled />
              </div>
            </div>
            <div class="col-md-3">
              <div class="form-group">
                <span class="form-group">
                  <label for="">Type</label>
                </span>
                <select class="form-control" :value="contactdetail.type" disabled>
                  <option
                    v-for="cdtype in contactDetailTypeOptions"
                    :key="`cdtype_option-${cdtype.value}`"
                    :value="cdtype.value"
                  >
                    {{ cdtype.display }}
                  </option>
                </select>
              </div>
            </div>
            <div class="col-md-3 text-left">
              <span
                class="glyphicon glyphicon-refresh"
                v-tooltip="'Sync Contact Detail'"
                style="color: green; cursor: pointer; margin-top: 32px; text-align: left"
                @click="addOldContactDetail(contactdetail)"
              >
              </span>
              Save to ezyVet
            </div>
          </div>
        </template>
      </div>
      <hr />
      <div v-if="hasAddresses">
        <h4>Addresses</h4>
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Name</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in getChartData('addresses')" :key="'address_' + index">
              <td class="text-left">{{ row['name'] }}</td>
              <td class="text-left">{{ row['value'] }}</td>
            </tr>
          </tbody>
        </table>
        <div class="form-group">
          <span class="form-group">
            <label>
              Changes to send to Farm Services
              <i
                class="glyphicon glyphicon-envelope"
                v-tooltip="'Email might be generated'"
                title="Email might be generated"
              >
              </i>
            </label>
            <comments
              field-name="address_notes_for_farm_services"
              :addEmailNotification="true"
              :isReadOnly="isReadOnly"
            >
            </comments>
          </span>
        </div>
        <div class="alert alert-info">
          Since addresses cannot be updated here please add details of any changes to addresses so that farm services
          can update client information in ezyVet. Changes to contact details will be noted here automatically. Please
          add information relating to communications or reminders.
        </div>
      </div>

      <div>
        <radio-input
          label="On farm deliveries?"
          field-name="wants_on_farm_deliveries"
          :addEmailNotification="true"
          :choices="[
            { value: 'Yes', display: 'Yes' },
            { value: 'No', display: 'No' },
          ]"
          :disabled="isReadOnly"
        >
        </radio-input>
        <text-area
          label="Contact person's name and number for delivery driver."
          field-name="contact_for_on_farm_deliveries"
          :disabled="isReadOnly"
        >
        </text-area>

        <br />
        <table class="table table-bordered" v-if="hasDeliveryInfo">
          <thead>
            <tr>
              <th>Shed</th>
              <th>Delivery</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(fsa, index) in getChartData('DELIVERY_INFO')" :key="'deliveryinfo' + index">
              <td class="text-left">{{ fsa['shed'] }}</td>
              <td class="text-left">{{ fsa['delivery'] }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import baseComponent from '@/mixins/base_consult_component_v2'

const CONTACT_DETAIL_FIELDS = ['name', 'value', 'contact_detail_type']

export default {
  name: 'ContactDetails',
  mixins: [baseComponent],
  data() {
    return {
      canAdd: true,
    }
  },
  created() {
    this.checkUnique()
    let newRequiredFields =
      (this.getFieldValue('CONTACT_DETAILS', this.$options.name) || [])
        .filter((cd) => this.getFieldValue(`contactdetail_${cd.id}-active`))
        .map((cd) => CONTACT_DETAIL_FIELDS.map((cdf) => `contactdetail_${cd.id}-${cdf}`))
        .flat() || []
    newRequiredFields.concat(
      (this.getFieldValue('NEW_CONTACT_DETAILS', this.$options.name) || [])
        .map((cd) => CONTACT_DETAIL_FIELDS.map((cdf) => `newcontactdetail_${cd.frontend_id}-${cdf}`))
        .flat()
    )
    this.addRequiredFields({
      stepName: 'ContactDetails',
      value: newRequiredFields,
    })
    this.checkAndCompleteStep()
  },
  mounted() {
    // TODO: temporary frontend migration - remove
    let notifications = this.getFieldValue('notifications', 'EmailNotifications')
    if (!notifications.find((notif) => notif.slug == 'wc-addresses-farm-services-notes')) {
      notifications.push({
        slug: 'wc-addresses-farm-services-notes',
        auto_generated: false,
        marked: false,
        name: 'Addresses Notes to Farm Services',
      })
      this.setFieldValue({
        fieldName: 'notifications',
        stepName: 'EmailNotifications',
        value: notifications,
      })
    }
    // END TODO
    // initialize wants_on_farm_deliveries
    if (!['Yes', 'No'].includes(this.getFieldValue('wants_on_farm_deliveries', this.$options.name))) {
      let wants_on_farm_deliveries =
        ((this.getFieldValue('CHARTS') || {}).DELIVERY_INFO || []).length > 0 ? 'Yes' : 'No'
      this.setFieldValue({
        fieldName: 'wants_on_farm_deliveries',
        stepName: this.$options.name,
        value: wants_on_farm_deliveries,
      })
    }
    let wants_on_farm_deliveries = ((this.getFieldValue('CHARTS') || {}).DELIVERY_INFO || []).length > 0 ? 'Yes' : 'No'
    if (!['Yes', 'No'].includes(this.getFieldValue('original-wants_on_farm_deliveries', this.$options.name))) {
      this.setFieldValue({
        fieldName: 'original-wants_on_farm_deliveries',
        stepName: this.$options.name,
        value: wants_on_farm_deliveries,
      })
    }
  },
  methods: {
    ...mapActions('currentConsult', ['deleteField', 'addRequiredFields', 'removeRequiredFields', 'setFieldComment']),
    subscriptionGroupsDisplay: function (contactdetail) {
      return (
        this.getFieldValue(this.prefixedField('subscription_groups', contactdetail.id, 'contactdetail')) || []
      ).join(', ')
    },
    toggleContactDetailActive: function (contactdetail) {
      let fieldsData = {
        stepName: this.$options.name,
        value: CONTACT_DETAIL_FIELDS.map((cdf) => `contactdetail_${contactdetail.id}-${cdf}`),
      }

      if (this.getFieldValue(`contactdetail_${contactdetail.id}-active`)) {
        this.addRequiredFields(fieldsData)
      } else {
        this.removeRequiredFields(fieldsData)
      }
      this.handleContactsChange()
    },
    validate() {
      // check all new contact details have values
      if (
        !this.newCDs.every((cd) =>
          CONTACT_DETAIL_FIELDS.every((field) =>
            this.getFieldValue(`newcontactdetail_${cd.frontend_id}-${field}`, 'ContactDetails')
          )
        )
      ) {
        return false
      }

      // check all old contact details have values
      if (
        !this.oldCDs
          .filter((cd) => this.getFieldValue(`contactdetail_${cd.id}-active`, this.$options.name))
          .every((cd) =>
            CONTACT_DETAIL_FIELDS.every((field) =>
              this.getFieldValue(`contactdetail_${cd.id}-${field}`, 'ContactDetails')
            )
          )
      ) {
        return false
      }

      // check `value` is unique for all active contact details
      return this.checkUnique()
    },
    checkUnique() {
      let valueFields = []
      for (let cd of this.oldCDs.filter((cd) =>
        this.getFieldValue(`contactdetail_${cd.id}-active`, this.$options.name)
      )) {
        if (this.getFieldValue(`contactdetail_${cd.id}-active`, 'ContactDetails')) {
          valueFields.push(`contactdetail_${cd.id}-value`)
        }
      }
      for (let cd of this.newCDs) {
        valueFields.push(`newcontactdetail_${cd.frontend_id}-value`)
      }
      let valuesToCheck = []
      for (let valueField of valueFields) {
        valuesToCheck.push(this.getFieldValue(valueField, 'ContactDetails'))
      }

      if (valuesToCheck.length != new Set(valuesToCheck).size) {
        return false
      }

      return true
    },
    addOldContactDetail: function (oldcontactdetail) {
      this.canAdd = false
      let newContactDetails = this.getFieldValue('NEW_CONTACT_DETAILS') || []
      let newConatctDetail = {
        frontend_id: Math.max(...newContactDetails.concat([{ frontend_id: 0 }]).map((cd) => cd.frontend_id)) + 1,
      }
      this.setFieldValue({ fieldName: 'NEW_CONTACT_DETAILS', value: newContactDetails.concat([newConatctDetail]) })
      let allNewFields = []
      for (let field of CONTACT_DETAIL_FIELDS) {
        let fieldName = `newcontactdetail_${newConatctDetail.frontend_id}-${field}`
        let value = field == 'contact_detail_type' ? oldcontactdetail.type : oldcontactdetail[field]
        this.setFieldValue({
          fieldName: fieldName,
          value: value,
        })
        allNewFields.push(fieldName)
      }
      this.setFieldValue({
        stepName: 'ContactDetails',
        fieldName: `newcontactdetail_${newConatctDetail.frontend_id}-active`,
        value: true,
      })
      this.addRequiredFields({
        stepName: 'ContactDetails',
        value: allNewFields.concat([`newcontactdetail_${newConatctDetail.frontend_id}-active`]),
      })
      // remove after adding to sync

      this.setFieldValue({
        fieldName: 'OLD_CONTACT_DETAILS',
        value: this.getFieldValue('OLD_CONTACT_DETAILS', this.$options.name).filter((cd) => cd != oldcontactdetail),
      })
      this.handleContactsChange()
      this.canAdd = true
    },
    addNewContactDetail() {
      this.canAdd = false
      let newContactDetails = this.getFieldValue('NEW_CONTACT_DETAILS')
      let newConatctDetail = {
        frontend_id: Math.max(...newContactDetails.concat([{ frontend_id: 0 }]).map((cd) => cd.frontend_id)) + 1,
      }
      this.setFieldValue({ fieldName: 'NEW_CONTACT_DETAILS', value: newContactDetails.concat([newConatctDetail]) })
      let allNewFields = []
      for (let field of CONTACT_DETAIL_FIELDS) {
        let fieldName = `newcontactdetail_${newConatctDetail.frontend_id}-${field}`
        this.setFieldValue({
          fieldName: fieldName,
          value: null,
        })
        allNewFields.push(fieldName)
      }
      this.setFieldValue({
        stepName: 'ContactDetails',
        fieldName: `newcontactdetail_${newConatctDetail.frontend_id}-active`,
        value: true,
      })
      this.addRequiredFields({
        stepName: 'ContactDetails',
        value: allNewFields.concat([`newcontactdetail_${newConatctDetail.frontend_id}-active`]),
      })
      this.checkAndCompleteStep()
      this.canAdd = true
    },
    removeNewContactDetail(newcontactdetail) {
      let newContactDetails = this.getFieldValue('NEW_CONTACT_DETAILS').filter(
        (cd) => cd.frontend_id != newcontactdetail.frontend_id
      )
      this.setFieldValue({ fieldName: 'NEW_CONTACT_DETAILS', value: newContactDetails })

      let allNewFields = []
      for (let field of CONTACT_DETAIL_FIELDS) {
        let fieldName = `newcontactdetail_${newcontactdetail.frontend_id}-${field}`
        this.deleteField({ fieldName })
        allNewFields.push(fieldName)
      }
      this.removeRequiredFields({
        stepName: 'ContactDetails',
        value: allNewFields.concat([`newcontactdetail_${newcontactdetail.frontend_id}-active`]),
      })
      this.handleContactsChange()
    },
    ...mapMutations('currentConsult', ['addContact', 'editContact', 'addRole', 'editRole', 'removeContact']),
    toggleNotifications: function () {
      this.setEmailNotification({
        slug: 'wc-addresses-farm-services-notes',
        value: Boolean(this.getFieldComment('address_notes_for_farm_services', this.$options.name).current),
      })

      let send_on_farm_deliveries_email = Boolean(
        this.getFieldValue('wants_on_farm_deliveries', this.$options.name) !=
          this.getFieldValue('original-wants_on_farm_deliveries', this.$options.name) ||
          (this.getFieldValue('contact_for_on_farm_deliveries', this.$options.name) || '').trim()
      )

      this.setFieldValue({
        fieldName: 'send_on_farm_deliveries_email',
        modelName: this.$options.name,
        value: send_on_farm_deliveries_email,
      })
      this.setEmailNotification({
        slug: 'programs-page-on-farm-deliveries',
        value: send_on_farm_deliveries_email,
      })
    },
    handleContactsChange: function () {
      let commentName = 'address_notes_for_farm_services'
      const SEPARATOR = '========================='
      let currentComment = (this.getFieldComment(commentName, this.$options.name).current || '').split(SEPARATOR)[0]

      this.setFieldComment({
        fieldName: commentName,
        modelName: this.$options.name,
        value:
          currentComment.trim() +
          '\n' +
          SEPARATOR +
          '\n' +
          (this.getFieldValue('NEW_CONTACT_DETAILS', this.$options.name) || [])
            .map((cd) => {
              let name = this.getFieldValue(`newcontactdetail_${cd.frontend_id}-name`) || ''
              let cdtypeid = this.getFieldValue(`newcontactdetail_${cd.frontend_id}-contact_detail_type`) || ''
              let type = (this.contactDetailTypeOptions.find((cdt) => cdt.value == cdtypeid) || {}).display
              let value = this.getFieldValue(`newcontactdetail_${cd.frontend_id}-value`) || ''
              return `A new contact method was posted to ezyVet: ${name} ${type} ${value}`
            })
            .join('\n'),
      })
      this.checkAndCompleteStep()
    },
  },
  computed: {
    ...mapGetters('currentConsult', ['isReadOnly', 'getFieldComment']),
    subscriptionGroupsOptions: function () {
      return ['Accounts', 'General', 'Reminders'].map((sg) => {
        return { display: sg, value: sg }
      })
    },
    hasDeliveryInfo: function () {
      return ((this.getFieldValue('CHARTS') || {})['DELIVERY_INFO'] || []).length > 0
    },
    hasAddresses: function () {
      return ((this.getFieldValue('CHARTS') || {})['addresses'] || []).length > 0
    },
    hasOldContactDetails: function () {
      return (this.getFieldValue('OLD_CONTACT_DETAILS', this.$options.name) || []).length > 0
    },
    oldCDs: function () {
      return this.getFieldValue('CONTACT_DETAILS', 'ContactDetails') || []
    },
    newCDs: function () {
      return this.getFieldValue('NEW_CONTACT_DETAILS', 'ContactDetails') || []
    },
    contactDetailTypeOptions: function () {
      return (this.getFieldValue('CONTACT_DETAIL_TYPES', this.$options.name) || []).map((cd) => {
        return { value: cd.id, display: cd.name }
      })
    },
  },
}
</script>
